import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, Star, GripVertical, Image, ImageOff, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { publicApi, adminApi } from '@/lib/api';
import { convertGoogleDriveUrl, getMediaType } from '@/lib/urlUtils';
import { FileUpload, MultiFileUpload } from '@/components/FileUpload';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { toast } from 'sonner';

// Thumbnail component for media preview
const MediaThumbnail = ({ url, type }) => {
  const [error, setError] = useState(false);
  const convertedUrl = convertGoogleDriveUrl(url);
  
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted">
        <ImageOff className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }
  
  if (type === 'video') {
    return (
      <video 
        src={convertedUrl} 
        className="w-full h-full object-cover"
        muted
        onError={() => setError(true)}
      />
    );
  }
  
  return (
    <img 
      src={convertedUrl} 
      alt="Media thumbnail"
      className="w-full h-full object-cover"
      onError={() => setError(true)}
    />
  );
};

const AdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'dashboard';
  
  // State
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [cats, projs, tests, logos, adms, settings] = await Promise.all([
        publicApi.getCategories(),
        adminApi.getProjects(),
        publicApi.getTestimonials(),
        publicApi.getClientLogos(),
        adminApi.getAdmins(),
        adminApi.getSettings(),
      ]);
      setCategories(cats);
      setProjects(projs);
      setTestimonials(tests);
      setClientLogos(logos);
      setAdmins(adms);
      setSiteSettings(settings);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type, item = null) => {
    setDialogType(type);
    setEditItem(item);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogType(null);
    setEditItem(null);
  };

  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };

  // Stats for dashboard
  const stats = [
    { title: 'Categories', value: categories.length, color: 'bg-blue-500' },
    { title: 'Projects', value: projects.length, color: 'bg-green-500' },
    { title: 'Featured', value: projects.filter(p => p.is_featured).length, color: 'bg-primary' },
    { title: 'Testimonials', value: testimonials.length, color: 'bg-purple-500' },
    { title: 'Client Logos', value: clientLogos.length, color: 'bg-yellow-500' },
    { title: 'Admins', value: admins.length, color: 'bg-red-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <Tabs value={currentTab} onValueChange={handleTabChange}>
        <TabsList className="mb-8 hidden">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
          <TabsTrigger value="logos">Client Logos</TabsTrigger>
          <TabsTrigger value="admins">Admins</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="mb-8">
            <h1 className="text-3xl font-bold">
              <span className="font-serif uppercase tracking-wider">Admin</span>
              <span className="font-script text-primary text-4xl ml-2">Dashboard</span>
            </h1>
            <p className="text-muted-foreground mt-2">Overview of your portfolio content</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card data-testid={`stat-${stat.title.toLowerCase()}`}>
                  <CardContent className="pt-6">
                    <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center text-white mb-3`}>
                      <span className="text-xl font-bold">{stat.value}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Recent Projects</CardTitle>
                <CardDescription>Latest added projects</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {projects.slice(0, 5).map((project) => (
                    <div key={project.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted">
                      <img src={project.thumbnail} alt={project.title} className="w-12 h-12 rounded object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{project.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {project.is_draft ? 'Draft' : 'Published'}
                          {project.is_featured && ' • Featured'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common tasks</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full justify-start" variant="outline" onClick={() => { handleTabChange('projects'); openDialog('project'); }}>
                  <Plus className="mr-2 h-4 w-4" /> Add New Project
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => { handleTabChange('categories'); openDialog('category'); }}>
                  <Plus className="mr-2 h-4 w-4" /> Add New Category
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => { handleTabChange('testimonials'); openDialog('testimonial'); }}>
                  <Plus className="mr-2 h-4 w-4" /> Add Testimonial
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => { handleTabChange('logos'); openDialog('logo'); }}>
                  <Plus className="mr-2 h-4 w-4" /> Add Client Logo
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <CategoriesSection
            categories={categories}
            onAdd={() => openDialog('category')}
            onEdit={(item) => openDialog('category', item)}
            onDelete={(item) => setDeleteConfirm({ type: 'category', item })}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects">
          <ProjectsSection
            projects={projects}
            categories={categories}
            onAdd={() => openDialog('project')}
            onEdit={(item) => openDialog('project', item)}
            onDelete={(item) => setDeleteConfirm({ type: 'project', item })}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Testimonials Tab */}
        <TabsContent value="testimonials">
          <TestimonialsSection
            testimonials={testimonials}
            onAdd={() => openDialog('testimonial')}
            onEdit={(item) => openDialog('testimonial', item)}
            onDelete={(item) => setDeleteConfirm({ type: 'testimonial', item })}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Client Logos Tab */}
        <TabsContent value="logos">
          <ClientLogosSection
            logos={clientLogos}
            onAdd={() => openDialog('logo')}
            onEdit={(item) => openDialog('logo', item)}
            onDelete={(item) => setDeleteConfirm({ type: 'logo', item })}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Admins Tab */}
        <TabsContent value="admins">
          <AdminsSection
            admins={admins}
            onAdd={() => openDialog('admin')}
            onDelete={(item) => setDeleteConfirm({ type: 'admin', item })}
            onRefresh={fetchAllData}
          />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <SettingsSection
            settings={siteSettings}
            onRefresh={fetchAllData}
          />
        </TabsContent>
      </Tabs>

      {/* Edit/Add Dialogs */}
      <ItemDialog
        open={dialogOpen}
        type={dialogType}
        item={editItem}
        categories={categories}
        onClose={closeDialog}
        onSave={fetchAllData}
      />

      {/* Delete Confirmation with Captcha */}
      <DeleteConfirmDialog
        open={!!deleteConfirm}
        onOpenChange={(open) => !open && setDeleteConfirm(null)}
        itemType={deleteConfirm?.type || ''}
        itemName={deleteConfirm?.item?.title || deleteConfirm?.item?.name || deleteConfirm?.item?.client_name || deleteConfirm?.item?.username}
        isDeleting={isDeleting}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            const { type, item } = deleteConfirm;
            if (type === 'category') await adminApi.deleteCategory(item.id);
            else if (type === 'project') await adminApi.deleteProject(item.id);
            else if (type === 'testimonial') await adminApi.deleteTestimonial(item.id);
            else if (type === 'logo') await adminApi.deleteClientLogo(item.id);
            else if (type === 'admin') await adminApi.deleteAdmin(item.id);
            toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
            fetchAllData();
          } catch (error) {
            toast.error(error.message);
          } finally {
            setIsDeleting(false);
            setDeleteConfirm(null);
          }
        }}
      />
    </div>
  );
};

// Section Components
const CategoriesSection = ({ categories, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-2xl font-bold">
          <span className="font-serif uppercase tracking-wider">Manage</span>
          <span className="font-script text-primary text-3xl ml-2">Categories</span>
        </h2>
        <p className="text-muted-foreground">Add and manage project categories</p>
      </div>
      <Button onClick={onAdd} data-testid="add-category-btn">
        <Plus className="mr-2 h-4 w-4" /> Add Category
      </Button>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {categories.map((category, index) => (
        <Card key={category.id} className="group relative" data-testid={`category-card-${index}`}>
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 mx-auto rounded-full overflow-hidden mb-3 border-2 border-primary/20">
              <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
            </div>
            <p className="font-medium text-sm truncate">{category.name}</p>
            <p className="text-xs text-muted-foreground">Order: {category.order}</p>
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(category)} data-testid={`edit-category-${index}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onDelete(category)} data-testid={`delete-category-${index}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const ProjectsSection = ({ projects, categories, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-2xl font-bold">
          <span className="font-serif uppercase tracking-wider">Manage</span>
          <span className="font-script text-primary text-3xl ml-2">Projects</span>
        </h2>
        <p className="text-muted-foreground">Add and manage portfolio projects</p>
      </div>
      <Button onClick={onAdd} data-testid="add-project-btn">
        <Plus className="mr-2 h-4 w-4" /> Add Project
      </Button>
    </div>

    <div className="space-y-4">
      {projects.map((project, index) => (
        <Card key={project.id} className="overflow-hidden" data-testid={`project-card-${index}`}>
          <div className="flex">
            <div className="w-32 h-32 flex-shrink-0">
              <img src={project.thumbnail} alt={project.title} className="w-full h-full object-cover" />
            </div>
            <CardContent className="flex-1 py-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{project.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{project.description}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {project.categories?.map(cat => (
                      <Badge key={cat} variant="secondary" className="text-xs">{cat}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {project.is_featured && (
                    <Badge className="bg-primary"><Star className="h-3 w-3 mr-1" /> Featured</Badge>
                  )}
                  {project.is_draft && (
                    <Badge variant="outline"><EyeOff className="h-3 w-3 mr-1" /> Draft</Badge>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => onEdit(project)} data-testid={`edit-project-${index}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => onDelete(project)} data-testid={`delete-project-${index}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </div>
        </Card>
      ))}
    </div>
  </div>
);

const TestimonialsSection = ({ testimonials, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-2xl font-bold">
          <span className="font-serif uppercase tracking-wider">Manage</span>
          <span className="font-script text-primary text-3xl ml-2">Testimonials</span>
        </h2>
        <p className="text-muted-foreground">Add and manage client testimonials</p>
      </div>
      <Button onClick={onAdd} data-testid="add-testimonial-btn">
        <Plus className="mr-2 h-4 w-4" /> Add Testimonial
      </Button>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      {testimonials.map((testimonial, index) => (
        <Card key={testimonial.id} className="group relative" data-testid={`testimonial-card-${index}`}>
          <CardContent className="pt-6">
            <p className="text-muted-foreground italic mb-4">"{testimonial.content}"</p>
            <div className="flex items-center gap-3">
              {testimonial.avatar && (
                <img src={testimonial.avatar} alt={testimonial.client_name} className="w-10 h-10 rounded-full object-cover" />
              )}
              <div>
                <p className="font-medium">{testimonial.client_name}</p>
                <p className="text-sm text-muted-foreground">{testimonial.company}</p>
              </div>
            </div>
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(testimonial)} data-testid={`edit-testimonial-${index}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onDelete(testimonial)} data-testid={`delete-testimonial-${index}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const ClientLogosSection = ({ logos, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-2xl font-bold">
          <span className="font-serif uppercase tracking-wider">Manage</span>
          <span className="font-script text-primary text-3xl ml-2">Client Logos</span>
        </h2>
        <p className="text-muted-foreground">Add and manage client logos</p>
      </div>
      <Button onClick={onAdd} data-testid="add-logo-btn">
        <Plus className="mr-2 h-4 w-4" /> Add Logo
      </Button>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {logos.map((logo, index) => (
        <Card key={logo.id} className="group relative" data-testid={`logo-card-${index}`}>
          <CardContent className="pt-6 text-center">
            <div className="h-16 flex items-center justify-center mb-3">
              <img src={logo.image} alt={logo.name} className="max-h-full max-w-full object-contain" />
            </div>
            <p className="font-medium text-sm truncate">{logo.name}</p>
            <p className="text-xs text-muted-foreground">Order: {logo.order}</p>
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(logo)} data-testid={`edit-logo-${index}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onDelete(logo)} data-testid={`delete-logo-${index}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const AdminsSection = ({ admins, onAdd, onDelete }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-2xl font-bold">
          <span className="font-serif uppercase tracking-wider">Manage</span>
          <span className="font-script text-primary text-3xl ml-2">Admins</span>
        </h2>
        <p className="text-muted-foreground">Add and remove admin users</p>
      </div>
      <Button onClick={onAdd} data-testid="add-admin-btn">
        <Plus className="mr-2 h-4 w-4" /> Add Admin
      </Button>
    </div>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {admins.map((admin, index) => (
        <Card key={admin.id} className="group relative" data-testid={`admin-card-${index}`}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-lg font-bold text-primary">{admin.username[0].toUpperCase()}</span>
              </div>
              <div>
                <p className="font-medium">{admin.username}</p>
                <p className="text-sm text-muted-foreground">Administrator</p>
              </div>
            </div>
            {admins.length > 1 && (
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onDelete(admin)} data-testid={`delete-admin-${index}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

// Settings Section Component
const SettingsSection = ({ settings, onRefresh }) => {
  const [formData, setFormData] = useState(settings || {});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.updateSettings(formData);
      toast.success('Settings updated successfully');
      onRefresh();
    } catch (error) {
      toast.error(error.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">
          <span className="font-serif uppercase tracking-wider">Site</span>
          <span className="font-script text-primary text-3xl ml-2">Settings</span>
        </h2>
        <p className="text-muted-foreground">Manage contact information displayed on the website</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
            <CardDescription>This information will be displayed on the Contact Us page</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  value={formData.email || ''} 
                  onChange={(e) => handleChange('email', e.target.value)} 
                  placeholder="hello@example.com"
                  data-testid="settings-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  value={formData.phone || ''} 
                  onChange={(e) => handleChange('phone', e.target.value)} 
                  placeholder="+1 (234) 567-890"
                  data-testid="settings-phone"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Instagram Handle</Label>
                <Input 
                  value={formData.instagram || ''} 
                  onChange={(e) => handleChange('instagram', e.target.value)} 
                  placeholder="@yourusername"
                  data-testid="settings-instagram"
                />
              </div>
              <div className="space-y-2">
                <Label>Instagram URL</Label>
                <Input 
                  value={formData.instagram_url || ''} 
                  onChange={(e) => handleChange('instagram_url', e.target.value)} 
                  placeholder="https://instagram.com/yourusername"
                  data-testid="settings-instagram-url"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input 
                value={formData.location || ''} 
                onChange={(e) => handleChange('location', e.target.value)} 
                placeholder="Your business address"
                data-testid="settings-location"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Working Hours</CardTitle>
            <CardDescription>Set your business hours</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Weekdays</Label>
              <Input 
                value={formData.working_hours_weekday || ''} 
                onChange={(e) => handleChange('working_hours_weekday', e.target.value)} 
                placeholder="Monday - Friday: 9:00 AM - 6:00 PM"
                data-testid="settings-hours-weekday"
              />
            </div>
            <div className="space-y-2">
              <Label>Saturday</Label>
              <Input 
                value={formData.working_hours_saturday || ''} 
                onChange={(e) => handleChange('working_hours_saturday', e.target.value)} 
                placeholder="Saturday: 10:00 AM - 4:00 PM"
                data-testid="settings-hours-saturday"
              />
            </div>
            <div className="space-y-2">
              <Label>Sunday</Label>
              <Input 
                value={formData.working_hours_sunday || ''} 
                onChange={(e) => handleChange('working_hours_sunday', e.target.value)} 
                placeholder="Sunday: Closed"
                data-testid="settings-hours-sunday"
              />
            </div>
            <div className="space-y-2">
              <Label>Response Time Note</Label>
              <Textarea 
                value={formData.response_time || ''} 
                onChange={(e) => handleChange('response_time', e.target.value)} 
                placeholder="We typically respond to inquiries within 24-48 hours."
                rows={2}
                data-testid="settings-response-time"
              />
            </div>
          </CardContent>
        </Card>

        <Button type="submit" disabled={loading} data-testid="settings-save-btn">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Settings
        </Button>
      </form>
    </div>
  );
};

// Item Dialog Component
const ItemDialog = ({ open, type, item, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [mediaUrls, setMediaUrls] = useState([]);

  useEffect(() => {
    if (type === 'project' && item) {
      setFormData(item);
      setMediaUrls(item.media || []);
    } else if (item) {
      setFormData(item);
    } else {
      setFormData({});
      setMediaUrls([]);
    }
  }, [type, item, open]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let data = { ...formData };
      if (type === 'project') {
        data.media = mediaUrls;
      }

      if (item) {
        // Update
        if (type === 'category') await adminApi.updateCategory(item.id, data);
        else if (type === 'project') await adminApi.updateProject(item.id, data);
        else if (type === 'testimonial') await adminApi.updateTestimonial(item.id, data);
        else if (type === 'logo') await adminApi.updateClientLogo(item.id, data);
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} updated`);
      } else {
        // Create
        if (type === 'category') await adminApi.createCategory(data);
        else if (type === 'project') await adminApi.createProject(data);
        else if (type === 'testimonial') await adminApi.createTestimonial(data);
        else if (type === 'logo') await adminApi.createClientLogo(data);
        else if (type === 'admin') await adminApi.createAdmin(data);
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} created`);
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    switch (type) {
      case 'category':
        return (
          <>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={formData.name || ''} onChange={(e) => handleChange('name', e.target.value)} required data-testid="category-name-input" />
            </div>
            <FileUpload
              value={formData.image || ''}
              onChange={(url) => handleChange('image', url)}
              folder="categories"
              label="Category Image"
              data-testid="category-image-input"
            />
            <div className="space-y-2">
              <Label>Order</Label>
              <Input type="number" value={formData.order || 0} onChange={(e) => handleChange('order', parseInt(e.target.value))} data-testid="category-order-input" />
            </div>
          </>
        );

      case 'project':
        return (
          <>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={formData.title || ''} onChange={(e) => handleChange('title', e.target.value)} required data-testid="project-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description || ''} onChange={(e) => handleChange('description', e.target.value)} required rows={3} data-testid="project-description-input" />
            </div>
            <FileUpload
              value={formData.thumbnail || ''}
              onChange={(url) => handleChange('thumbnail', url)}
              folder="projects/thumbnails"
              label="Thumbnail Image"
              data-testid="project-thumbnail-input"
            />
            <div className="space-y-2">
              <Label>Categories</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="project-categories-input"
                  >
                    {formData.categories?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {formData.categories.map((cat) => (
                          <Badge key={cat} variant="secondary" className="text-xs">
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Select categories...</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search categories..." />
                    <CommandList>
                      <CommandEmpty>No categories found.</CommandEmpty>
                      <CommandGroup>
                        {categories.map((category) => {
                          const isSelected = formData.categories?.includes(category.name);
                          return (
                            <CommandItem
                              key={category.id}
                              onSelect={() => {
                                const current = formData.categories || [];
                                if (isSelected) {
                                  handleChange('categories', current.filter(c => c !== category.name));
                                } else {
                                  handleChange('categories', [...current, category.name]);
                                }
                              }}
                            >
                              <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${isSelected ? 'bg-primary border-primary' : 'border-primary'}`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              {category.name}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {formData.categories?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.categories.map((cat) => (
                    <Badge key={cat} variant="secondary" className="text-xs flex items-center gap-1">
                      {cat}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-destructive" 
                        onClick={() => handleChange('categories', formData.categories.filter(c => c !== cat))}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <MultiFileUpload
              value={mediaUrls}
              onChange={setMediaUrls}
              folder="projects/media"
              label="Additional Media (Images & Videos)"
            />
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={formData.is_featured || false} onCheckedChange={(checked) => handleChange('is_featured', checked)} data-testid="project-featured-switch" />
                <Label>Featured</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.is_draft || false} onCheckedChange={(checked) => handleChange('is_draft', checked)} data-testid="project-draft-switch" />
                <Label>Draft</Label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Order</Label>
              <Input type="number" value={formData.order || 0} onChange={(e) => handleChange('order', parseInt(e.target.value))} data-testid="project-order-input" />
            </div>
          </>
        );

      case 'testimonial':
        return (
          <>
            <div className="space-y-2">
              <Label>Client Name</Label>
              <Input value={formData.client_name || ''} onChange={(e) => handleChange('client_name', e.target.value)} required data-testid="testimonial-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Company</Label>
              <Input value={formData.company || ''} onChange={(e) => handleChange('company', e.target.value)} required data-testid="testimonial-company-input" />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea value={formData.content || ''} onChange={(e) => handleChange('content', e.target.value)} required rows={4} data-testid="testimonial-content-input" />
            </div>
            <FileUpload
              value={formData.avatar || ''}
              onChange={(url) => handleChange('avatar', url)}
              folder="testimonials"
              label="Avatar Image (optional)"
              data-testid="testimonial-avatar-input"
            />
            <div className="space-y-2">
              <Label>Order</Label>
              <Input type="number" value={formData.order || 0} onChange={(e) => handleChange('order', parseInt(e.target.value))} data-testid="testimonial-order-input" />
            </div>
          </>
        );

      case 'logo':
        return (
          <>
            <div className="space-y-2">
              <Label>Client Name</Label>
              <Input value={formData.name || ''} onChange={(e) => handleChange('name', e.target.value)} required data-testid="logo-name-input" />
            </div>
            <FileUpload
              value={formData.image || ''}
              onChange={(url) => handleChange('image', url)}
              folder="logos"
              label="Logo Image"
              data-testid="logo-image-input"
            />
            <div className="space-y-2">
              <Label>Order</Label>
              <Input type="number" value={formData.order || 0} onChange={(e) => handleChange('order', parseInt(e.target.value))} data-testid="logo-order-input" />
            </div>
          </>
        );

      case 'admin':
        return (
          <>
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={formData.username || ''} onChange={(e) => handleChange('username', e.target.value)} required data-testid="admin-username-input" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={formData.password || ''} onChange={(e) => handleChange('password', e.target.value)} required data-testid="admin-password-input" />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const getTitle = () => {
    const action = item ? 'Edit' : 'Add';
    const typeName = type?.charAt(0).toUpperCase() + type?.slice(1);
    return `${action} ${typeName}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="item-dialog">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 overflow-hidden">
          {renderForm()}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="dialog-cancel">Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="dialog-submit">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {item ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;
